import type { App } from 'vue'
import { isAxiosError, isCancel } from 'axios'
import * as Sentry from '@sentry/vue'
import { CODIGOS_TECNICOS_ERRO } from '@next/compartilhados/observabilidade'
import type { ContextoErro, IdentidadeObservabilidade, Observabilidade } from '@next/compartilhados/observabilidade'
import type { ConfiguracaoObservabilidade } from '@next/compartilhados/observabilidade/configuracao'
import { criarControleVolume } from '@next/compartilhados/observabilidade/controle-volume'
import { classificarErro } from '@next/compartilhados/observabilidade/politica-erros'
import { FalhaHttp } from '@next/infraestrutura/http'
import { assinaturaEvento, contextoDoEvento, sanitizarEvento } from './evento-sentry'
import { criarTransporte } from './transporte'
import type { SaidaConsole } from './transporte'
import { naturezaFalhaHttp, obterContextoRequisicaoHttp } from './contexto-http'
import { projetarExcecao } from './projecao-erro'
import type { ProjetarErroLegado } from './projecao-erro'

export type NormalizarErroLegado = (erro: unknown, detalhes: unknown) => ContextoErro | undefined
type Dependencias = Readonly<{
  configuracao: ConfiguracaoObservabilidade
  origemAplicacao: string
  normalizarLegado?: NormalizarErroLegado
  obterErroOriginal?: (erro: unknown) => unknown
  projetarErroLegado?: ProjetarErroLegado
  obterIdentidade?: () => IdentidadeObservabilidade | undefined
  saidaConsole?: SaidaConsole
  enviar?: typeof fetch
  agora?: () => number
}>

let donoAtivo: object | undefined

export const criarObservabilidadeSentry = (dependencias: Dependencias) => {
  const controle = criarControleVolume(dependencias.agora)
  const contextos = new WeakMap<object, ContextoErro>()
  let cliente: ReturnType<typeof Sentry.init>
  let ativo = false
  let ciclo = 0
  const cicloAtivo = (esperado: number) => ativo && ciclo === esperado
  const dono = {}
  /** Caminho comum de uma captura manual, Vue ou JavaScript. null encerra o relato. */
  const prepararEvento: NonNullable<Sentry.BrowserOptions['beforeSend']> = async (evento, hint) => {
    if (!ativo) return null
    const cicloDaCaptura = ciclo
    try {
      const contexto = obterContextoErro(hint.originalException, evento)
      if (!deveRegistrarIncidente(contexto)) return null

      // Identidade lida por evento, sem guardar uma cópia da sessão no scope do SDK.
      // Sobrescreve inclusive um setUser legado que tenha ficado de outro login.
      let identidade: IdentidadeObservabilidade | undefined
      try { identidade = dependencias.obterIdentidade?.() } catch { /* Ainda registrar o erro sem identidade. */ }
      let original = hint.originalException
      try {
        original = dependencias.obterErroOriginal?.(original) ?? original
      } catch { /* Relato independe da referência original. */ }
      const projecao = cliente ? await projetarExcecao(evento, hint, contexto, cliente, dependencias.projetarErroLegado) : { evento }
      if (!cicloAtivo(cicloDaCaptura)) return null
      const seguro = sanitizarEvento({
        ...projecao.evento,
        // Não herdar contextos de outras capturas ou do scope do SDK.
        contexts: {},
        user: identidade ? {
          id: identidade.usuario.id,
          ...(identidade.usuario.email ? { email: identidade.usuario.email } : {}),
        } : {},
        tags: {
          ...evento.tags,
          empresa_id: identidade?.empresa?.id,
          empresa_nome: identidade?.empresa?.nome,
        },
      }, { ...contexto, ...(projecao.origemStack ? { origemStack: projecao.origemStack } : {}) }, dependencias.origemAplicacao)
      if (!seguro) { controle.registrarDescarte(); return null }

      const assinatura = assinaturaEvento(seguro)
      if (!controle.permitir(assinatura, original)) return null
      return seguro
    } catch {
      controle.registrarDescarte()
      return null
    }
  }


  const obterContextoErro = (erro: unknown, evento: Sentry.Event): ContextoErro => {
    const contextoManual = erro && typeof erro === 'object' ? contextos.get(erro) : undefined
    // Metadado do SDK 10.73.0: identifica o handler sem copiar contexto/props do Vue.
    const mecanismo = evento.exception?.values?.[0]?.mechanism?.type
    let origemAutomatica: ContextoErro['origem'] | undefined
    if (mecanismo === 'auto.function.vue.error_handler') origemAutomatica = 'vue'
    else if (mecanismo?.startsWith('auto.browser.')) origemAutomatica = 'javascript'

    let inferido: ContextoErro | undefined
    if (erro instanceof FalhaHttp) {
      const causaHttp = isAxiosError(erro.causa) ? erro.causa : undefined
      const codigo = CODIGOS_TECNICOS_ERRO.find(valor => valor === causaHttp?.code)
      const cancelamento = erro.tipo === 'cancelamento' || isCancel(erro.causa)
        || (erro.causa instanceof Error && erro.causa.name === 'AbortError')
      let natureza: ContextoErro['naturezaFalha'] = erro.tipo === 'cancelamento' ? undefined : erro.tipo
      if (causaHttp && erro.tipo !== 'resposta_invalida') natureza = naturezaFalhaHttp(erro.status, codigo)
      else if (erro.tipo === 'resposta_invalida' && erro.status === undefined && erro.causa instanceof Error) natureza = 'desconhecida'
      inferido = {
        origem: 'http', operacao: 'requisicao',
        categoria: cancelamento ? 'cancelamento' : 'inesperado',
        ...(natureza ? { naturezaFalha: natureza } : {}),
        ...(codigo ? { codigoTecnico: codigo } : {}),
        ...(erro.status !== undefined ? { statusHttp: erro.status } : {}),
        ...(erro.mensagemRecebida ? { mensagemApi: erro.mensagemRecebida } : {}),
        ...(erro.codigoRecebido ? { codigoApi: erro.codigoRecebido } : {}),
        ...(erro.requestId ? { requestId: erro.requestId } : {}),
        servicoHttp: erro.servico,
        ...(erro.metodo ? { metodoHttp: erro.metodo } : {}),
        ...(erro.endpoint ? { endpoint: erro.endpoint } : {}),
        ...obterContextoRequisicaoHttp(causaHttp?.config ?? {}),
      }
    } else {
      inferido = dependencias.normalizarLegado?.(erro, evento.extra)
    }

    // Do menos específico ao mais específico; os campos ausentes são completados.
    const contexto: ContextoErro = {
      ...contextoDoEvento({ ...evento, contexts: {} }),
      ...inferido,
      ...(origemAutomatica ? { origem: origemAutomatica } : {}),
      ...contextoManual,
    }
    // A captura manual não transforma cancelamento/validação comprovados em incidente.
    if (inferido?.categoria === 'cancelamento' || inferido?.categoria === 'validacao') {
      return { ...contexto, categoria: inferido.categoria }
    }
    return contexto
  }

  const deveRegistrarIncidente = (contexto: ContextoErro): boolean => {
    const classificacao = classificarErro(contexto)
    if (classificacao === 'incidente') return true
    if (classificacao === 'qualidade') controle.registrarQualidade()
    else controle.registrarDescarte()
    return false
  }

  const porta: Observabilidade = {
    registrarErro(erro, contexto) {
      if (!ativo) return
      try {
        // Primitivos são envolvidos para transportar o contexto sem alterar scope global.
        const excecao = erro && typeof erro === 'object' ? erro : new Error(String(erro))
        contextos.set(excecao, contexto)
        // Qualidade/descarte explícitos param aqui, sem precisar construir um evento no SDK.
        if (!deveRegistrarIncidente(contexto)) return
        Sentry.captureException(excecao)
      } catch { controle.registrarDescarte() }
    },
  }

  return {
    porta,
    ativar(app: App): void {
      if (ativo || donoAtivo || dependencias.configuracao.destino === 'desativado') return
      try {
        ativo = true
        donoAtivo = dono
        const cicloAtual = ++ciclo
        cliente = Sentry.init({
          app,
          dsn: dependencias.configuracao.destino === 'apm' ? dependencias.configuracao.dsn : 'https://local@observabilidade.invalid/1',
          sampleRate: dependencias.configuracao.destino === 'apm' ? 0.1 : 1,
          defaultIntegrations: false,
          integrations: [Sentry.vueIntegration(), Sentry.globalHandlersIntegration(), Sentry.browserApiErrorsIntegration()],
          tracePropagationTargets: [],
          attachProps: false,
          sendDefaultPii: false,
          sendClientReports: false,
          enableLogs: false,
          maxBreadcrumbs: 0,
          maxValueLength: 4096,
          transport: criarTransporte({
            configuracao: dependencias.configuracao,
            origemAplicacao: dependencias.origemAplicacao,
            estaAtivo: () => cicloAtivo(cicloAtual),
            registrarDescarte: () => { controle.registrarDescarte() },
            saidaConsole: dependencias.saidaConsole,
            enviar: dependencias.enviar,
          }),
          beforeSend: (evento, hint) => ciclo === cicloAtual ? prepararEvento(evento, hint) : null,
        })
      } catch { ativo = false; donoAtivo = undefined; controle.registrarDescarte() }
    },
    desativar(): void {
      ativo = false
      if (donoAtivo === dono) donoAtivo = undefined
      if (cliente) void Promise.resolve(cliente.close(1000)).catch(() => false)
    },
    async aguardarEnvios(): Promise<boolean> {
      try { return cliente ? await cliente.flush(1000) : true } catch { return false }
    },
    obterContagens: controle.obterContagens,
  }
}
